import { DateTime } from 'luxon';
import { createActionHelpers } from 'vuex-loading';
import { processDocument } from '@/features/documents/utils';
import Vue from 'vue';
import Service from './service';

const { startLoading, endLoading } = createActionHelpers({
  moduleName: 'loading',
});

const CLEAR_MODULE = 'CLEAR_MODULE';
const UPDATE_ERROR = 'UPDATE_ERROR';
const UPDATE_OPERATION = 'UPDATE_OPERATION';
const UPDATE_LOADING = 'UPDATE_LOADING';

export const clearModuleAction = 'clearModuleAction';
export const initOperationAction = 'initOperationAction';
export const updateOperationAction = 'updateOperationAction';
export const updateDatesAssigmentsAction = 'updateDatesAssigmentsAction';
export const passToAnalisisAction = 'passToAnalisisAction';

const processOperation = (op) => {
  const updatedPayload = {};
  updatedPayload.operation = op;
  if (op.included && op.included.datesAssigments) {
    updatedPayload.datesAssigments = op.included.datesAssigments;
  }
  if (op.included && op.included.clients) {
    updatedPayload.clients = op.included.clients;
  }
  if (op.included && op.included.pledges) {
    updatedPayload.garantias = op.included.pledges;
  }
  if (op.included && op.included.expenses) {
    updatedPayload.expenses = op.included.expenses;
  }
  if (op.included && op.included.volume) {
    updatedPayload.volume = op.included.volume;
  }
  if (op.included && op.included.prereport) {
    updatedPayload.prereport = op.included.prereport;
  }
  if (op.included && op.included.report) {
    updatedPayload.report = op.included.report;
  }
  if (op.included && op.included.documents && op.documentsNeeded) {
    const docsNeeded = {};
    op.documentsNeeded.forEach((docId) => {
      if (docId in op.included.documents) {
        docsNeeded[docId] = processDocument(op.included.documents[docId]);
      }
    });
    updatedPayload.documentsNeeded = docsNeeded;
  }
  if (op.included && op.included.documents && op.documents) {
    const docs = {};
    op.documents.forEach((docId) => {
      if (docId in op.included.documents) {
        docs[docId] = op.included.documents[docId];
      }
    });
    updatedPayload.documents = docs;
  }
  if (op.included && op.included.letras && op.letras) {
    updatedPayload.letras = op.included.letras;
  }
  return updatedPayload;
};

export default {
  namespaced: true,
  state: {
    error: '',
    id: '',
    loading: true,
    status: '',
    statusName: '',
    operationData: null,
    datesAssigmentsData: {},
    clientsData: {},
    garantiasData: {},
    calculosData: {},
    gastosData: {},
    preReports: {},
    reports: {},
    letras: {},
    documentsData: null,
    documentsNeededData: {},
    selectedOperation: null,
  },
  mutations: {
    selectOperationCommit(state, payload) {
      Vue.set(state, 'selectedOperation', payload);
    },
    [CLEAR_MODULE](state) {
      Vue.set(state, 'id', '');
      Vue.set(state, 'error', '');
      Vue.set(state, 'status', '');
      Vue.set(state, 'loading', true);
      Vue.set(state, 'statusName', '');
      Vue.set(state, 'operationData', null);
      Vue.set(state, 'datesAssigmentsData', {});
      Vue.set(state, 'clientsData', {});
      Vue.set(state, 'garantiasData', {});
      Vue.set(state, 'calculosData', {});
      Vue.set(state, 'gastosData', {});
      Vue.set(state, 'preReports', {});
      Vue.set(state, 'reports', {});
      Vue.set(state, 'letras', {});
      Vue.set(state, 'documentsData', {});
      Vue.set(state, 'documentsNeededData', {});
    },
    [UPDATE_ERROR](state, error) {
      state.error = error;
    },
    [UPDATE_LOADING](state, payload) {
      state.loading = payload;
    },
    [UPDATE_OPERATION](state, payload) {
      const {
        operation,
        datesAssigments,
        clients,
        garantias,
        expenses,
        volume,
        prereport,
        report,
        letras,
        documents,
        documentsNeeded,
      } = payload;
      if (!operation) {
        return;
      }
      if (operation.id) {
        Vue.set(state, 'id', operation.id);
      }
      if (operation.state) {
        Vue.set(state, 'status', operation.state);
      }
      if (operation.statusName) {
        Vue.set(state, 'statusName', operation.statusName);
      }
      Vue.set(state, 'operationData', operation);
      if (datesAssigments) {
        Vue.set(state, 'datesAssigmentsData', datesAssigments);
      }
      if (clients) {
        Vue.set(state, 'clientsData', clients);
      }
      if (garantias) {
        Vue.set(state, 'garantiasData', garantias);
      }
      if (expenses) {
        Vue.set(state, 'gastosData', expenses);
      }
      if (volume) {
        Vue.set(state, 'calculosData', volume);
      }
      if (prereport) {
        Vue.set(state, 'preReports', prereport);
      }
      if (report) {
        Vue.set(state, 'reports', report);
      }
      if (letras) {
        Vue.set(state, 'letras', letras);
      }
      if (documents) {
        Vue.set(state, 'documentsData', documents);
      }
      if (documentsNeeded) {
        Vue.set(state, 'documentsNeededData', documentsNeeded);
      }
    },
  },
  actions: {
    async selectOperationAction({ commit, rootGetters, dispatch }, payload) {
      const localOps = await rootGetters['operation_model/entitiesGetter'];
      if (localOps[payload]) {
        commit('selectOperationCommit', localOps[payload]);
      } else {
        const serverOps = await dispatch('operation_model/fetchAction', {
          uuidFilter: {
            path: 'uuid',
            operator: '=',
            value: payload,
          },
        }, { root: true });
        if (Array.isArray(serverOps.entities) && serverOps.entities.length > 0) {
          commit('selectOperationCommit', serverOps.entities[0]);
        }
      }
    },
    [clearModuleAction]({ commit }) {
      commit('CLEAR_MODULE');
    },
    [updateOperationAction]({ dispatch }, payload) {
      return Service.updateOperation(payload)
        .then(() => dispatch(initOperationAction, payload.id));
    },
    [updateDatesAssigmentsAction]({ dispatch, state, rootGetters }, payload) {
      startLoading(dispatch, 'update dates');
      const collaborators = rootGetters['collaborator/colaborators'];
      const idColaborator = collaborators.filter(e => e.title === payload.colaborador);
      if (idColaborator.length > 0) {
        payload.colaborador = idColaborator[0].id;
      }
      dispatch('datesAssignements_model/upsertEntityAction', payload, { root: true })
        .then(() => {
          endLoading(dispatch, 'update dates');
        })
        .catch((error) => {
          dispatch('updateErrorOverlay', error, { root: true });
          endLoading(dispatch, 'update dates');
        });
    },
    [initOperationAction]({ commit, dispatch, rootGetters }, payload) {
      startLoading(dispatch, 'operation');
      commit(CLEAR_MODULE);
      return Service.getOperationData(payload)
        .then((op) => {
          if (op.included.datesAssigments && op.included.datesAssigments.colaborador) {
            const collaborators = rootGetters['collaborator/colaborators'];
            const titleColaborator = collaborators.filter(e => e.id === op.included.datesAssigments.colaborador);
            if (titleColaborator && titleColaborator.length > 0) {
              op.included.datesAssigments.colaborador = titleColaborator[0].title;
            }
          }
          const updatedPayload = processOperation(op);

          commit(UPDATE_OPERATION, updatedPayload);
          endLoading(dispatch, 'operation');
        })
        .catch((error) => {
          commit(UPDATE_ERROR, 'Error en el servicio.');
          endLoading(dispatch, 'operation');
          throw error;
        });
    },
    [passToAnalisisAction]({ commit, state, dispatch, rootGetters }) {
      if (state.id !== '') {
        const status = rootGetters['taxonomy/searchTaxonomyValue']({ vid: 'status', text: 'Análisis' });
        const operation = {
          id: state.id,
          state: status,
        };
        return dispatch('updateOperationAction', operation);
      }
      const errorMsg = 'No se puede pasar la operación a análisis';
      commit(UPDATE_ERROR, errorMsg);
      return Promise.reject(errorMsg);
    },
  },
  getters: {
    operation: state => state.operationData,
    datesAssigments: state => state.datesAssigmentsData,
    clients: state => state.clientsData,
    garantias: state => state.garantiasData,
    loading: state => state.loading,
    expenses: state => state.gastosData,
    calculos: state => state.calculosData,
    documents: state => state.documentsData,
    documentsNeededData: state => state.documentsNeededData,
    error: state => state.error,
    preReports: state => state.preReports,
    reports: state => state.reports,
    letras: (state) => {
      const list = [];
      Vue._.forOwn(state.letras, (value) => {
        list.push(value);
      });
      return list;
    },
    fechaVencimiento: (state, getters, e, rootGetters) => {
      if (rootGetters['taxonomy/getTaxonomy'] &&
        Array.isArray(rootGetters['taxonomy/getTaxonomy']('date')) &&
        state.operationData &&
        state.operationData.nexumTipoPlazo &&
        state.datesAssigmentsData &&
        state.datesAssigmentsData.notarySignature) {
        let fecha = DateTime.fromISO(state.datesAssigmentsData.notarySignature);
        rootGetters['taxonomy/getTaxonomy']('date').forEach((tipo) => {
          if (state.operationData.nexumTipoPlazo === tipo.uuid) {
            switch (tipo.text) {
              case 'meses':
                fecha = fecha.plus({ months: state.operationData.nexumPlazo });
                break;
              case 'años':
                fecha = fecha.plus({ years: state.operationData.nexumPlazo });
                break;
              case 'días':
                fecha = fecha.plus({ days: state.operationData.nexumPlazo });
                break;
              default:
                break;
            }
          }
        });
        return fecha;
      }
      return null;
    },
  },
};



// WEBPACK FOOTER //
// ./src/features/operation/store.js